/** HTTP statuses that may mean "sign in to continue" for anonymous users on deep links. */
export const DEEP_LINK_AUTH_GATE_STATUSES = new Set([401, 403, 404]);

export type DeepLinkResourceKind =
  'sample' | 'project' | 'projectSample' | 'projectShipping' | 'shipping' | 'data' | 'publication';

export type ParsedDeepLink = {
  kind: DeepLinkResourceKind;
  /** Primary identifier shown in messages */
  label: string;
  searchPath: string;
  searchLabel: string;
};

/** Search/list navigation for each deep-link resource type (buttons on unavailable views). */
export function deepLinkSearchNavigation(
  kind: DeepLinkResourceKind
): Pick<ParsedDeepLink, 'searchPath' | 'searchLabel'> {
  switch (kind) {
    case 'sample':
    case 'projectSample':
      return { searchPath: '/samples', searchLabel: 'Sample search' };
    case 'project':
      return { searchPath: '/projects', searchLabel: 'Project search' };
    case 'shipping':
    case 'projectShipping':
      return { searchPath: '/shippings', searchLabel: 'Shipping search' };
    case 'data':
      return { searchPath: '/data', searchLabel: 'Data search' };
    case 'publication':
      return { searchPath: '/publications', searchLabel: 'Publication search' };
  }
}

function parsedDeepLink(kind: DeepLinkResourceKind, label: string): ParsedDeepLink {
  return { kind, label, ...deepLinkSearchNavigation(kind) };
}

export function isAnonymousUser(
  currentUser: { isAnonymous?: boolean; eppn?: string | null } | null | undefined
): boolean {
  return Boolean(currentUser?.isAnonymous || !currentUser?.eppn);
}

export function shouldPromptAnonymousSignIn(
  currentUser: { isAnonymous?: boolean; eppn?: string | null } | null | undefined,
  status?: number
): boolean {
  if (!isAnonymousUser(currentUser)) {
    return false;
  }
  if (status === undefined) {
    return true;
  }
  return DEEP_LINK_AUTH_GATE_STATUSES.has(status);
}

export function toRelativeAppPath(pathname: string, basePath: string): string {
  return basePath && pathname.startsWith(basePath) ? pathname.slice(basePath.length) : pathname;
}

/** Parse deep-link targets from an app-relative path (no basePath prefix). */
export function parseDeepLinkResource(relativePath: string): ParsedDeepLink | null {
  const pathOnly = relativePath.split('?')[0]?.split('#')[0] ?? relativePath;

  const sampleMatch = pathOnly.match(/^\/samples\/([^/]+)/);
  if (sampleMatch) {
    return parsedDeepLink('sample', decodeURIComponent(sampleMatch[1]));
  }

  const projectShippingMatch = pathOnly.match(/^\/projects\/([^/]+)\/shippings\/([^/]+)/);
  if (projectShippingMatch) {
    return parsedDeepLink('projectShipping', decodeURIComponent(projectShippingMatch[2]));
  }

  const projectSampleMatch = pathOnly.match(/^\/projects\/([^/]+)\/([^/]+)/);
  if (projectSampleMatch && projectSampleMatch[2] !== 'shippings' && projectSampleMatch[2] !== 'sample-requests') {
    return parsedDeepLink('projectSample', decodeURIComponent(projectSampleMatch[2]));
  }

  const projectMatch = pathOnly.match(/^\/projects\/([^/]+)/);
  if (projectMatch) {
    return parsedDeepLink('project', decodeURIComponent(projectMatch[1]));
  }

  const shippingMatch = pathOnly.match(/^\/shippings\/([^/]+)/);
  if (shippingMatch) {
    return parsedDeepLink('shipping', decodeURIComponent(shippingMatch[1]));
  }

  const dataMatch = pathOnly.match(/^\/data\/(.+)/);
  if (dataMatch) {
    return parsedDeepLink('data', decodeURIComponent(dataMatch[1]));
  }

  const publicationMatch = pathOnly.match(/^\/publications\/(.+)/);
  if (publicationMatch) {
    return parsedDeepLink('publication', decodeURIComponent(publicationMatch[1]));
  }

  return null;
}

/** Shown on ResourceUnavailableView — we cannot distinguish missing vs forbidden from exists probes. */
export const DEEP_LINK_UNAVAILABLE_EXPLANATION =
  'It might not exist, the link may be incorrect, or you may not have permission to view it.';

export const DEEP_LINK_ANONYMOUS_SIGN_IN_SUFFIX = 'If you have an account, sign in to continue.';

export function deepLinkUnavailableHeadline(kind: DeepLinkResourceKind): string {
  switch (kind) {
    case 'sample':
    case 'projectSample':
      return 'This sample could not be found';
    case 'project':
      return 'This project could not be found';
    case 'shipping':
    case 'projectShipping':
      return 'This shipping could not be found';
    case 'data':
      return 'This dataset could not be found';
    case 'publication':
      return 'This publication could not be found';
  }
}

/** Identifier only — no claim about existence or access (exists endpoints merge both cases). */
export function deepLinkUnavailableDetail(kind: DeepLinkResourceKind, label: string): string {
  switch (kind) {
    case 'sample':
    case 'projectSample':
      return `Sample: ${label}`;
    case 'project':
      return `Project: ${label}`;
    case 'shipping':
    case 'projectShipping':
      return `Shipping: ${label}`;
    case 'data':
      return `Dataset: ${label}`;
    case 'publication':
      return `Publication: ${label}`;
  }
}

export function deepLinkAnonymousSignInContext(
  kind: DeepLinkResourceKind,
  label: string
): { title: string; message: string } {
  const noun = deepLinkResourceNoun(kind);
  return {
    title: deepLinkUnavailableHeadline(kind),
    message: `${noun} ${label} might not exist, or you may need to sign in to view it. ${DEEP_LINK_ANONYMOUS_SIGN_IN_SUFFIX}`,
  };
}

function deepLinkResourceNoun(kind: DeepLinkResourceKind): string {
  switch (kind) {
    case 'sample':
    case 'projectSample':
      return 'Sample';
    case 'project':
      return 'Project';
    case 'shipping':
    case 'projectShipping':
      return 'Shipping';
    case 'data':
      return 'Dataset';
    case 'publication':
      return 'Publication';
  }
}

/** exists endpoints return false for both missing resources and missing read access. */
export const DEEP_LINK_EXISTS_PROBE_FAILED_STATUS = 404;
